import {
    Box,
    Chip,
    Link,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FetchOrderlinesDataWrapper } from 'src/app/components/data-wrappers/orderline/FetchOrderlinesDataWrapper';
import { useLinkOrderlinesDataWrapper } from 'src/app/components/data-wrappers/orderline/LinkOrderlinesDataWrapper';
import { useUnlinkOrderlineDataWrapper } from 'src/app/components/data-wrappers/orderline/UnlinkOrderlineDataWrapper';
import useApiFetch from 'src/app/hooks/useApiFetch';
import ApiResponseBody from 'src/data/api/responses/ApiResponseBody';
import PaginatedApiResponseBody from 'src/data/api/responses/PaginatedApiResponseBody';
import PaginationMeta from 'src/data/api/responses/PaginationMeta';
import Orderline from 'src/data/models/orderline/orderline';
import orderlineService from 'src/data/services/orderlineService';
import { BUTTON_SIZES } from 'src/shared/theme/enums';
import Button from 'src/view/components/button/Button';
import SearchInput from 'src/view/components/search-input/SearchInput';

export interface LinkOrderlinesFeatureProps {
    eventId: string;
    orderline: Orderline;
    onGroupChanged?: () => void;
    onSelectOrderline?: (orderline: Orderline) => void;
}

const blockRowSeat = ({ blockNumber, rowNumber, seatNumber }: Orderline) =>
    [blockNumber, rowNumber, seatNumber].filter(Boolean).join(' / ') || '-';

const notLinkedMessage = 'This orderline is not linked to any other orderline.';

export default function LinkOrderlinesFeature({
    eventId,
    orderline,
    onGroupChanged,
    onSelectOrderline,
}: Readonly<LinkOrderlinesFeatureProps>): JSX.Element {
    const [searchTerm, setSearchTerm] = useState('');
    const [linkGroupId, setLinkGroupId] = useState(orderline.linkGroupId);
    const [invalidatedAt, setInvalidatedAt] = useState<number>();

    const [{ loading: searching, data: searchResults }, fetchSearchResults] =
        useApiFetch<PaginatedApiResponseBody<Orderline, PaginationMeta>>();

    const [, fetchOrderline] = useApiFetch<ApiResponseBody<Orderline>>();

    // FetchOrderlinesDataWrapper cannot drive this: its useDeepCompareEffect throws when every
    // dependency is primitive, which an event-scoped search by term is.
    const search = useCallback(() => {
        if (!searchTerm) return;

        fetchSearchResults(
            orderlineService.fetchOrderlines({
                filter: [{ property: 'eventId', value: eventId }],
                q: searchTerm,
            }),
            { useDefaultErrorHandler: true }
        );
    }, [searchTerm, eventId, fetchSearchResults]);

    useEffect(() => {
        search();
    }, [search]);

    const refreshGroup = useCallback(async () => {
        // Link and unlink return 204, so re-read the order line to learn the group it landed in.
        const response = await fetchOrderline(
            orderlineService.fetchOrderlineById(orderline.orderId, orderline.id)
        );

        setLinkGroupId(response?.data.linkGroupId);
        setInvalidatedAt(Date.now());
        search();
        onGroupChanged?.();
    }, [fetchOrderline, orderline.orderId, orderline.id, search, onGroupChanged]);

    const { linkOrderlines, loading: linking } = useLinkOrderlinesDataWrapper({
        onSuccess: refreshGroup,
    });

    const { unlinkOrderline, loading: unlinking } = useUnlinkOrderlineDataWrapper({
        onSuccess: refreshGroup,
    });

    const renderRows = (
        rows: Orderline[],
        renderAction: (row: Orderline) => JSX.Element,
        emptyMessage: string,
        onClickOrderNumber?: (row: Orderline) => void
    ) => {
        if (rows.length === 0) {
            return (
                <Typography variant="body2" color="text.secondary">
                    {emptyMessage}
                </Typography>
            );
        }

        return (
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Order number</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Block/Row/Seat</TableCell>
                        <TableCell align="right" />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.id}>
                            <TableCell>
                                {onClickOrderNumber ? (
                                    <Link
                                        component="button"
                                        type="button"
                                        variant="body2"
                                        underline="hover"
                                        onClick={() => onClickOrderNumber(row)}
                                    >
                                        {row.orderSummary?.orderNumber}
                                    </Link>
                                ) : (
                                    row.orderSummary?.orderNumber
                                )}
                            </TableCell>
                            <TableCell>{row.seatingPlanCategoryName}</TableCell>
                            <TableCell>{row.quantity}</TableCell>
                            <TableCell>{blockRowSeat(row)}</TableCell>
                            <TableCell align="right">{renderAction(row)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    };

    const renderLinkAction = (row: Orderline) => {
        if (row.seatingPlanCategoryId !== orderline.seatingPlanCategoryId) {
            return (
                <Tooltip title="Orderlines can only be linked within the same category.">
                    <span>
                        <Chip size="small" variant="outlined" disabled label="Different category" />
                    </span>
                </Tooltip>
            );
        }

        if (linkGroupId && row.linkGroupId) {
            if (row.linkGroupId === linkGroupId) {
                return <Chip size="small" variant="outlined" disabled label="Already linked" />;
            }

            return (
                <Tooltip
                    title={
                        onSelectOrderline
                            ? 'Groups cannot be merged. Open this orderline to unlink it from its ' +
                              'current group, then link it here.'
                            : 'Groups cannot be merged. Unlink this orderline from its current ' +
                              'group before linking it here.'
                    }
                >
                    <Chip
                        size="small"
                        variant="outlined"
                        color="warning"
                        label="In another group"
                        onClick={onSelectOrderline && (() => onSelectOrderline(row))}
                    />
                </Tooltip>
            );
        }

        return (
            <Button
                size={BUTTON_SIZES.TINY}
                variant="outlined"
                disabled={linking}
                onClick={() => linkOrderlines(orderline.id, row.id)}
            >
                Link
            </Button>
        );
    };

    const renderUnlinkAction = (row: Orderline) => (
        <Button
            size={BUTTON_SIZES.TINY}
            variant="outlined"
            disabled={unlinking}
            onClick={() => unlinkOrderline(row.id)}
        >
            Unlink
        </Button>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="subtitle2">Currently linked</Typography>
                {linkGroupId ? (
                    <FetchOrderlinesDataWrapper
                        eventId={eventId}
                        filter={[{ property: 'linkGroupId', value: linkGroupId }]}
                        invalidatedAt={invalidatedAt}
                    >
                        {({ loading, data }) => {
                            const members = (data?.data || []).filter((o) => o.id !== orderline.id);

                            if (loading) {
                                return <Typography variant="body2">Loading…</Typography>;
                            }

                            return renderRows(
                                members,
                                renderUnlinkAction,
                                notLinkedMessage,
                                onSelectOrderline
                            );
                        }}
                    </FetchOrderlinesDataWrapper>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        {notLinkedMessage}
                    </Typography>
                )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="subtitle2">Link another orderline</Typography>
                <SearchInput placeholder="Search by order number" onChange={setSearchTerm} />

                {searchTerm &&
                    (searching ? (
                        <Typography variant="body2">Loading…</Typography>
                    ) : (
                        renderRows(
                            (searchResults?.data || []).filter((o) => o.id !== orderline.id),
                            renderLinkAction,
                            'No other orderlines found for this order number.'
                        )
                    ))}
            </Box>
        </Box>
    );
}
